// ── Video review ────────────────────────────────────────────────────
// Pairing a session's impact records against video of the session, so each
// detection can be confirmed by eye.
//
// This is the step the header-detection work is blocked on. The device's dry-run
// gates and per-event features are all recorded but unvalidated: nothing so far
// says which of those events was actually a header. That answer only comes from
// watching the video, and watching it is only affordable if clicking an impact
// jumps straight to the moment it happened.
//
// Two numbers make the whole thing work. Line up ONE known moment -- an impact
// you can see land -- and every other record follows, because both clocks run at
// the same rate. Everything below is that one subtraction, plus somewhere to
// keep what you decided.

// Where a record's timestamps come from: `timestampMs` is epoch ms once the Pi
// has synced the clock, and uptime ms otherwise. Either way it is one continuous
// monotonic clock for the whole boot, so a single offset covers every session in
// an export -- there is no need to re-sync per session.

import { t } from "i18n";

/** A record's identity across renders and reloads. Mirrors the key the record
 *  detail view uses for its own reset logic. */
export function recordKey(rec) {
  if (!rec) return null;
  return `${rec.sessionId}:${rec.impactId}:${rec.frameIndex}`;
}

// The five things an impact can turn out to be. NOT_HEADER and FALSE_POSITIVE
// are deliberately separate: a ball off the chest is the device correctly
// detecting a real impact that happens not to be a header, while a patch slap
// is the device firing at nothing. Folding them together would make it
// impossible to tell "the gate needs tuning" from "the detector is wrong",
// which are different problems with different fixes.
export const REVIEW_LABEL = {
  UNREVIEWED: "unreviewed",
  HEADER: "header",
  NOT_HEADER: "not_header",
  FALSE_POSITIVE: "false_positive",
  PRE_SESSION: "pre_session",
  UNSURE: "unsure",
};

// Display text per label. Getters so the text is translated at read time.
export const REVIEW_LABEL_TEXT = Object.defineProperties(
  {},
  Object.fromEntries(
    Object.values(REVIEW_LABEL).map((value) => [
      value,
      {
        enumerable: true,
        get: () => t(`utils.videoReview.labels.${value}`),
      },
    ])
  )
);

/** True when this impact still needs a human decision. */
export function needsCheck(rec, review) {
  const entry = review.labels[recordKey(rec)];
  return !entry || entry.label === REVIEW_LABEL.UNREVIEWED;
}

/** How the device's dry-run gate verdict squares with what the video showed.
 *
 * This is the measurement the whole exercise exists for. `gateFlagsLabel` is
 * the firmware's prediction ("pass" = it would have let this through); the
 * review is the truth. Four outcomes, and only the disagreements matter:
 *
 *   agree      -- gate passed a header, or rejected a non-header
 *   would_drop -- gate would have REJECTED a real header. The expensive error:
 *                 tighten a threshold on this evidence and you lose real data.
 *   would_keep -- gate would have passed something that was not a header. The
 *                 cheap error, and the one the gates exist to reduce.
 *
 * Pre-session records are excluded entirely: the device was not on a head, so
 * scoring the gate against them would measure nothing.
 */
export function gateAgreement(rec, label) {
  if (!label || label === REVIEW_LABEL.UNREVIEWED) return null;
  if (label === REVIEW_LABEL.PRE_SESSION || label === REVIEW_LABEL.UNSURE) {
    return null;
  }
  const gate = rec.gateFlagsLabel;
  if (!gate || gate === "unknown" || gate === "not evaluated") return null;
  const gatePassed = gate === "pass";
  const isHeader = label === REVIEW_LABEL.HEADER;
  if (gatePassed === isHeader) return "agree";
  return gatePassed ? "would_keep" : "would_drop";
}

/** Tally the gate's performance across everything reviewed so far. */
export function gateScore(records, review) {
  let agree = 0;
  let wouldDrop = 0;
  let wouldKeep = 0;
  for (const r of records) {
    const label = review.labels[recordKey(r)]?.label;
    const a = gateAgreement(r, label);
    if (a === "agree") agree++;
    else if (a === "would_drop") wouldDrop++;
    else if (a === "would_keep") wouldKeep++;
  }
  return { agree, wouldDrop, wouldKeep, scored: agree + wouldDrop + wouldKeep };
}

/** Build a sync from one anchor: the session instant of a record you can see
 *  land, and the video position where you see it. */
export function makeSync(anchorSessionMs, anchorVideoSec, meta = {}) {
  if (!Number.isFinite(anchorSessionMs) || !Number.isFinite(anchorVideoSec)) {
    return null;
  }
  if (anchorVideoSec < 0) return null;
  return { anchorSessionMs, anchorVideoSec, ...meta };
}

/** Video position (seconds) for a session instant, or null without a sync.
 *  May be negative or past the end -- that is a real answer meaning "this
 *  impact is outside the footage", and the caller should say so rather than
 *  silently clamping to 0 and scrubbing to an unrelated frame. */
export function videoTimeForSessionMs(sessionMs, sync) {
  if (!sync || !Number.isFinite(sessionMs)) return null;
  return sync.anchorVideoSec + (sessionMs - sync.anchorSessionMs) / 1000;
}

/** The inverse: which session instant a video position corresponds to. Used to
 *  log a header the device missed entirely, which is the only way to measure
 *  what fraction of real headers were caught. */
export function sessionMsForVideoTime(videoSec, sync) {
  if (!sync || !Number.isFinite(videoSec)) return null;
  return sync.anchorSessionMs + (videoSec - sync.anchorVideoSec) * 1000;
}

/** True when a record's moment actually falls inside the footage. */
export function isWithinVideo(sessionMs, sync, durationSec) {
  const t = videoTimeForSessionMs(sessionMs, sync);
  if (t == null) return false;
  if (!Number.isFinite(durationSec)) return t >= 0;
  return t >= 0 && t <= durationSec;
}

// ── Persistence ─────────────────────────────────────────────────────
// The video is a local file, so its blob URL dies on reload and it must be
// re-picked. The REVIEW must not: re-watching an hour of footage because a tab
// was closed is exactly the cost this tool exists to avoid. Keyed by device so
// two wearables reviewed on one machine do not overwrite each other.

const KEY_PREFIX = "sit.videoReview.";

export function reviewStorageKey(mac) {
  return `${KEY_PREFIX}${(mac || "unknown").toUpperCase()}`;
}

export function emptyReview() {
  // offsetMs: this unit's nudge onto the shared clock. One sync point lines
  // the FOOTAGE up with the session; a device whose clock was never synced,
  // or was synced against a different host, still needs its own trim on top.
  // Zero for anything that agrees with the others, which is the normal case.
  return { sync: null, labels: {}, missed: [], videoName: null, offsetMs: 0 };
}

export function loadReview(mac, storage) {
  const store = storage || safeStorage();
  if (!store) return emptyReview();
  try {
    const raw = store.getItem(reviewStorageKey(mac));
    if (!raw) return emptyReview();
    const parsed = JSON.parse(raw);
    return {
      ...emptyReview(),
      ...parsed,
      labels: parsed.labels || {},
      missed: Array.isArray(parsed.missed) ? parsed.missed : [],
      offsetMs: Number.isFinite(parsed.offsetMs) ? parsed.offsetMs : 0,
    };
  } catch {
    // A corrupt or unreadable entry must not take the screen down with it.
    return emptyReview();
  }
}

export function saveReview(mac, review, storage) {
  const store = storage || safeStorage();
  if (!store) return false;
  try {
    store.setItem(reviewStorageKey(mac), JSON.stringify(review));
    return true;
  } catch {
    return false;
  }
}

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

// ── Review state transitions (pure) ─────────────────────────────────

export function setLabel(review, key, label, note) {
  if (!key) return review;
  const prev = review.labels[key] || {};
  const next = { ...prev, label };
  if (note !== undefined) next.note = note;
  // Clearing back to unreviewed should leave nothing behind, so a cleared row
  // does not count toward "reviewed" in the progress figure.
  if (label === REVIEW_LABEL.UNREVIEWED && !next.note) {
    const labels = { ...review.labels };
    delete labels[key];
    return { ...review, labels };
  }
  return { ...review, labels: { ...review.labels, [key]: next } };
}

export function addMissed(review, sessionMs, note) {
  if (!Number.isFinite(sessionMs)) return review;
  const entry = { sessionMs, note: note || "" };
  const missed = [...review.missed, entry].sort(
    (a, b) => a.sessionMs - b.sessionMs
  );
  return { ...review, missed };
}

export function removeMissed(review, index) {
  return { ...review, missed: review.missed.filter((_, i) => i !== index) };
}

/** Fold the review carried inside a saved export file into whatever this
 *  browser already knows about the same device.
 *
 *  Both sides are legitimate: the file is the verdicts as they stood when it
 *  was saved (possibly on someone else's machine), local storage is the live
 *  copy being edited here. Where they disagree LOCAL WINS, because the local
 *  entry is the one a person is looking at and may have just changed --
 *  letting a file overwrite it would silently undo review work on open.
 *  Everything the file knows and local does not is taken as-is, which is the
 *  whole point: opening an export on a fresh machine restores the labelling.
 */
export function mergeReview(fileReview, localReview) {
  const local = { ...emptyReview(), ...(localReview || {}) };
  if (!fileReview) return local;
  const file = { ...emptyReview(), ...fileReview };

  // A missed header has no record and so no key -- dedupe on the instant it
  // was logged at, which is its only identity.
  const missed = [...file.missed];
  const seen = new Set(missed.map((m) => m.sessionMs));
  for (const m of local.missed) {
    if (!seen.has(m.sessionMs)) {
      missed.push(m);
      seen.add(m.sessionMs);
    }
  }
  missed.sort((a, b) => a.sessionMs - b.sessionMs);

  return {
    // The sync point is a property of one pairing of footage to session, so a
    // local one refers to footage actually open here and outranks the file's.
    sync: local.sync || file.sync,
    videoName: local.videoName || file.videoName,
    // Same rule as the sync: an alignment measured here is against footage
    // actually open here, so it outranks the file's.
    offsetMs: local.offsetMs || file.offsetMs || 0,
    labels: { ...file.labels, ...local.labels },
    missed,
  };
}

/** Counts for the progress line. `missed` is deliberately separate from the
 *  record tallies: a missed header has no record by definition, so folding it
 *  into "reviewed" would make the denominator meaningless. */
export function reviewProgress(review, records) {
  const total = records.length;
  let reviewed = 0;
  let headers = 0;
  let notHeaders = 0;
  let falsePositives = 0;
  let preSession = 0;
  let unsure = 0;
  for (const r of records) {
    const entry = review.labels[recordKey(r)];
    if (!entry || entry.label === REVIEW_LABEL.UNREVIEWED) continue;
    reviewed++;
    if (entry.label === REVIEW_LABEL.HEADER) headers++;
    else if (entry.label === REVIEW_LABEL.NOT_HEADER) notHeaders++;
    else if (entry.label === REVIEW_LABEL.FALSE_POSITIVE) falsePositives++;
    else if (entry.label === REVIEW_LABEL.PRE_SESSION) preSession++;
    else if (entry.label === REVIEW_LABEL.UNSURE) unsure++;
  }
  return {
    total,
    reviewed,
    headers,
    notHeaders,
    falsePositives,
    preSession,
    unsure,
    missed: review.missed.length,
    remaining: total - reviewed,
  };
}

/** mm:ss.s — video positions need tenths to line up an impact, and hours would
 *  be noise on training footage. */
export function fmtVideoTime(sec) {
  if (!Number.isFinite(sec)) return "--:--";
  const sign = sec < 0 ? "-" : "";
  const s = Math.abs(sec);
  const m = Math.floor(s / 60);
  return `${sign}${String(m).padStart(2, "0")}:${(s - m * 60)
    .toFixed(1)
    .padStart(4, "0")}`;
}

// ── Many units, one video ───────────────────────────────────────────
// A session is filmed once but worn by several athletes, so the natural unit
// of review is the footage, not the file. These fold N decoded exports into
// one time-ordered list while keeping each file's verdicts in its OWN review
// object -- which is what lets every file be saved back with its own tagging,
// and what stops unit A's "impact 3" colliding with unit B's.

/** Selection identity across sources. Records are only unique WITHIN a file:
 *  two units in the same session both have a `1:3:12`. */
export function sourceRecordKey(srcId, rec) {
  return `${srcId}::${recordKey(rec)}`;
}

/** Every source's records on one axis, oldest first.
 *
 *  `offsetMs` is the per-source nudge the compare view already uses for
 *  devices whose clocks were never synced: it shifts a track onto the shared
 *  axis, so the video sync point applies to all of them at once.
 */
export function mergeSourceRecords(sources) {
  const out = [];
  for (const src of sources || []) {
    for (const rec of src.records || []) {
      if (!Number.isFinite(rec?.timestampMs)) continue;
      out.push({
        srcId: src.id,
        src,
        rec,
        key: sourceRecordKey(src.id, rec),
        tMs: rec.timestampMs + (src.offsetMs || 0),
      });
    }
  }
  out.sort((a, b) => a.tMs - b.tMs);
  return out;
}

/** One progress line across every loaded file. Summing the per-source tallies
 *  rather than re-deriving them keeps a single definition of "reviewed". */
export function multiProgress(sources) {
  const zero = {
    total: 0,
    reviewed: 0,
    headers: 0,
    notHeaders: 0,
    falsePositives: 0,
    preSession: 0,
    unsure: 0,
    missed: 0,
    remaining: 0,
  };
  return (sources || []).reduce((acc, src) => {
    const p = reviewProgress(src.review, src.records || []);
    for (const k of Object.keys(zero)) acc[k] += p[k];
    return acc;
  }, zero);
}

/** The dry-run gate's record across every loaded file. */
export function multiGateScore(sources) {
  return (sources || []).reduce(
    (acc, src) => {
      const s = gateScore(src.records || [], src.review);
      acc.agree += s.agree;
      acc.wouldDrop += s.wouldDrop;
      acc.wouldKeep += s.wouldKeep;
      acc.scored += s.scored;
      return acc;
    },
    { agree: 0, wouldDrop: 0, wouldKeep: 0, scored: 0 }
  );
}

// ── Playback cues ───────────────────────────────────────────────────
// Once the footage is synced, every impact has a position IN THE VIDEO. That
// turns review from "click a row, watch, click the next row" into watching the
// session once and letting the impacts come to you.

/** Every impact as a video position, earliest first. Impacts before the
 *  footage starts are dropped: the playhead can never reach them, so keeping
 *  them would only ever fire at 0 and stop the tape before it began. */
export function cueList(items, sync) {
  if (!sync) return [];
  return (items || [])
    .map((item) => ({ item, t: videoTimeForSessionMs(item.tMs, sync) }))
    .filter((c) => Number.isFinite(c.t) && c.t >= 0)
    .sort((a, b) => a.t - b.t);
}

/** The first cue the playhead crossed moving from `prevSec` to `curSec`, or
 *  null.
 *
 *  Half-open on purpose: `(prev, cur]`. A cue exactly at `prev` has already
 *  been reported, which is what stops "pause at the impact, then resume" from
 *  pausing again on the same impact for ever. Going backwards reports nothing
 *  -- scrubbing back to look again is not a new crossing.
 */
export function crossedCue(cues, prevSec, curSec) {
  if (!(curSec > prevSec)) return null;
  for (const c of cues || []) {
    if (c.t > prevSec && c.t <= curSec) return c;
    if (c.t > curSec) break;
  }
  return null;
}
